import { useCallback, useState } from "react";
import { Share2 } from "lucide-react";
import { CURRICULUM } from "@/lib/curriculum";
import { posterFor } from "@/lib/journey";
import { ShareCardDialog, cardBase, loadImage } from "@/components/ShareCard";

export const drawStrip = async (x, y = 560, width = 1080) => {
  const tileW = 112;
  const tileH = 150;
  const gap = 12;
  const startX = (width - (8 * tileW + 7 * gap)) / 2;
  const posters = await Promise.all(CURRICULUM.map((m) => loadImage(posterFor(m.id)).catch(() => null)));
  posters.forEach((img, i) => {
    const px = startX + i * (tileW + gap);
    x.fillStyle = "#111";
    x.fillRect(px, y, tileW, tileH);
    if (img) {
      const s = Math.max(tileW / img.width, tileH / img.height);
      const dw = img.width * s;
      const dh = img.height * s;
      x.save();
      x.beginPath();
      x.rect(px, y, tileW, tileH);
      x.clip();
      x.drawImage(img, px + (tileW - dw) / 2, y + (tileH - dh) / 2, dw, dh);
      x.restore();
    }
    x.strokeStyle = "rgba(255,255,255,0.35)";
    x.lineWidth = 1;
    x.strokeRect(px, y, tileW, tileH);
    x.fillStyle = "#FFFFFF";
    x.font = "22px Georgia, serif";
    x.textAlign = "center";
    x.fillText(CURRICULUM[i].n, px + tileW / 2, y + tileH - 12);
  });
  const rule = x.createLinearGradient(startX, 0, startX + 8 * tileW + 7 * gap, 0);
  rule.addColorStop(0, "rgba(239,68,68,0.9)");
  rule.addColorStop(0.5, "rgba(245,158,11,0.9)");
  rule.addColorStop(1, "rgba(52,211,153,0.9)");
  x.fillStyle = rule;
  x.fillRect(startX, y + tileH + 14, 8 * tileW + 7 * gap, 3);
  x.font = "16px 'Courier New', monospace";
  x.textAlign = "left";
  x.fillStyle = "rgba(248,113,113,0.9)";
  x.fillText("A D V E R S I T Y", startX, y + tileH + 44);
  x.textAlign = "right";
  x.fillStyle = "rgba(110,231,183,0.95)";
  x.fillText("C H A N G E", startX + 8 * tileW + 7 * gap, y + tileH + 44);
  x.textAlign = "center";
};

export const drawGraduationCard = async (firstName) => {
  const { canvas, ctx: x } = await cardBase(posterFor("m8"));
  x.fillStyle = "#F59E0B";
  x.font = "30px 'Courier New', monospace";
  x.fillText("G R A D U A T E   ·   A L L   E I G H T", 540, 300);
  x.fillStyle = "#FFFFFF";
  x.font = "120px Georgia, serif";
  x.fillText("Through it.", 540, 450);
  await drawStrip(x);
  x.fillStyle = "#44403C";
  x.font = "italic 30px Georgia, serif";
  x.fillText("Eight rooms. Storm to forest. The practice is mine now.", 540, 860);
  x.fillStyle = "#F59E0B";
  x.font = "italic 40px Georgia, serif";
  x.fillText(`— ${firstName}, T.A.C.C. graduate`, 540, 940);
  x.fillStyle = "#78716C";
  x.font = "22px 'Courier New', monospace";
  x.fillText("F O R M U L A   T O   H E A L I N G   ·   C O M P L E T E", 540, 1040);
  return canvas;
};

export const GraduationShare = ({ firstName }) => {
  const [open, setOpen] = useState(false);
  const draw = useCallback(() => drawGraduationCard(firstName), [firstName]);
  return (
    <div className="flex flex-wrap items-center gap-3 mt-8" data-testid="graduation-share">
      <button onClick={() => setOpen(true)} data-testid="graduation-share-btn" className="btn-ember text-xs">
        <Share2 size={13} /> Share your graduation
      </button>
      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#78716C]">All eight scenes, storm to forest</span>
      <ShareCardDialog
        open={open}
        onOpenChange={setOpen}
        draw={draw}
        testId="graduation-card"
        title="You went through all eight. Say it."
        description="Your graduation card — every scene of the journey in one strip."
        fileName="TACC-graduate.png"
        shareText="All 8 modules of T.A.C.C. complete. Through Adversity Comes Change. tacc.love"
      />
    </div>
  );
};
